import {Component, DestroyRef, inject, Input, OnInit} from '@angular/core';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {ActivatedRoute, NavigationExtras, Router} from '@angular/router';
import {CommonModule, NgOptimizedImage} from '@angular/common';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {ProgressService} from '../../../shared/services/progress.service';
import {SnackbarService} from '../../../shared/services/snackbar.service';
import {DetailHeaderComponent} from "../../../shared/components/config-detail/detail-header/detail-header.component";
import {ConfigurationService} from "../../../shared/services/configuration.service";
import {
  ConfigDetailSkeletonComponent
} from "../../../shared/components/config-detail-skeleton/config-detail-skeleton.component";
import {AppSettingsDataService} from "../../services/app-settings-data.service";
import {Table} from "../../services/models/app-settings-service.model";
import {ConfigDetailComponent} from "../../../shared/components/config-detail/config-detail.component";
import {ConfigRouteEnum} from "../../../shared/enums/config-route.enum";
import {DetailBodyComponent} from "../../../shared/components/config-detail/detail-body/detail-body.component";
import {debounceTime, distinctUntilChanged} from "rxjs";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {ConfigWriteUserService} from "../../../shared/services/config-write-user.service";
import {RefreshService} from "../../../shared/services/refresh.service";
import {CardModule} from "primeng/card";
import {InputTextModule} from "primeng/inputtext";
import {IconField} from "primeng/iconfield";
import {InputIcon} from "primeng/inputicon";
import {ButtonModule} from "primeng/button";
import {TableModule} from "primeng/table";

@Component({
  selector: 'app-translation-detail',
  templateUrl: './app-settings-table.component.html',
  styleUrls: ['./app-settings-table.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    FormsModule,
    DetailHeaderComponent,
    NgOptimizedImage,
    ConfigDetailSkeletonComponent,
    ConfigDetailComponent,
    DetailBodyComponent,
    CardModule,
    InputTextModule,
    IconField,
    InputIcon,
    ButtonModule,
    TableModule,
  ]
})
export class AppSettingsTableComponent implements OnInit {
  @Input() navigateBack?: { commands: any[], extras?: NavigationExtras };

  protected readonly progressService = inject(ProgressService);
  private readonly snackBar = inject(SnackbarService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly appSettingsDataService = inject(AppSettingsDataService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly config = inject(ConfigurationService).configuration;
  private readonly destroyRef = inject(DestroyRef);
  private readonly configWriteUser = inject(ConfigWriteUserService);
  private readonly refreshService = inject(RefreshService);

  protected readonly ConfigRouteEnum = ConfigRouteEnum;

  searchId: string | undefined;
  dataSource: Table[] = [];
  selection: Table[] = [];
  searchInput = new FormControl('');
  logoUrl: string | null = this.config.app.defaultLogo;
  title: string | undefined;
  isAuthorized: boolean = this.configWriteUser.isAuthorized;

  private initialLoadDone = false;

  ngOnInit() {
    this.subscribeToRoute();
    this.setupSearch();
    this.loadHeaderInfo(this.searchId ?? '');

    this.refreshService.refresh$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.searchInput.setValue('');
      this.subscribeToRoute();
    });
  }

  private setupSearch(): void {
    this.searchInput.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
    ).subscribe(searchTerm => {
      this.loadAppSettingsTable(this.searchId ?? '', (searchTerm ?? '').trim().toLowerCase());
    });
  }

  private loadHeaderInfo(appName: string) {
    this.appSettingsDataService.findAllByApplicationNameSummary(appName).subscribe({
      next: (data: any[]) => {
        const settings = data?.[0];
        if (!settings) return;
        this.title = settings.applicationName || appName;
      }
    });
  }

  private subscribeToRoute() {
    this.activatedRoute.data.subscribe({
      next: data => {
        this.activatedRoute.paramMap.subscribe({
          next: paramMap => {
            let searchIdParam = paramMap.get('searchName');
            if (!searchIdParam) return;

            this.searchId = searchIdParam;
            this.initialLoadDone = false;
            this.loadAppSettingsTable(this.searchId);
          }
        })
      }
    })
  }

  loadAppSettingsTable(appName: string, searchTerm: string = ''): void {
    const showSkeleton = !this.initialLoadDone && !searchTerm;
    if (showSkeleton) {
      this.progressService.startLoading();
    }

    const applicationSettings = searchTerm
        ? this.appSettingsDataService.searchApplicationSettings(appName, searchTerm)
        : this.appSettingsDataService.findAllByApplicationNameSummary(appName);

    applicationSettings.subscribe({
      next: (data: any[]) => {
        this.dataSource = data as Table[];
        if (showSkeleton) {
          this.progressService.stopLoading();
          this.initialLoadDone = true;
        }
      },
      error: () => {
        this.snackBar.Error(this.translate.instant("snackbar.appSettings.error"));
        this.progressService.stopLoading();
      }
    });
  }

  protected addConfiguration() {
    this.router.navigate(['/', ConfigRouteEnum.APP_SETTINGS, 'new'], {
          queryParams: { applicationName: this.searchId }
        });
  }

  appsettingsDetail(row: Table) {
    this.router.navigate([row.id], {relativeTo: this.activatedRoute});
  }
}
